import { localeTo } from "@/lib/locale";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Crown, Check, Sparkles, Mail, LogOut } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { secureSignOut } from "@/lib/secure-sign-out";
import { isPreLaunchNow } from "@/lib/launch-mode";
import { getMyMembership } from "@/lib/membership.functions";
import { refreshMembership } from "@/lib/membership-refresh";
import {
  describeVerifiedPlan,
  type VerifiedPlan,
  type VerifiedPlanRow,
} from "@/lib/verified-plan";


/**
 * Step 4 of 4 — the post-payment confirmation home for paid Members.
 * The platform is live, so this page confirms membership and points
 * straight into the member area.
 */
export const Route = createFileRoute("/youre-in")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "You're In — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Your Membership is confirmed — your Recovery Trauma™ access is ready.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: YoureInPage,
});

function YoureInPage() {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState<string | null>(null);
  const [plan, setPlan] = useState<VerifiedPlan | null>(null);
  const navigate = useNavigate();
  const fetchMembership = useServerFn(getMyMembership);

  // Once the platform is live, this holding page must not trap Members
  // (bookmarks, emailed links, an open tab). Send them to the dashboard.
  useEffect(() => {
    if (isPreLaunchNow()) return;
    navigate({ to: "/dashboard", replace: true });
  }, [navigate]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setEmail(data.user?.email ?? null));
  }, []);

  // Read the webhook-verified membership row so the plan price and next
  // charge shown here are the member's real billing terms, not fixed copy.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const m = (await fetchMembership()) as VerifiedPlanRow | null;
        if (cancelled) return;
        const described = describeVerifiedPlan(m);
        if (m?.email) setEmail(m.email);
        if (described) {
          setPlan(described);
          if (described.isActive) refreshMembership();
        }
      } catch {
        /* keep the page usable without live plan detail */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchMembership]);


  async function signOut() {
    try { await secureSignOut(queryClient); } catch { /* noop */ }
    window.location.replace(localeTo("/"));
  }

  return (
    <div className="min-h-[100svh] bg-gradient-to-b from-background via-background to-[var(--gold-soft)]/25 px-4 py-14 sm:py-20">
      <div className="mx-auto flex max-w-[720px] items-center justify-center">
        <div className="w-full rounded-[2.25rem] border border-gold/40 bg-gradient-to-b from-[var(--gold-soft)]/50 via-card/95 to-card/90 px-6 py-10 text-center shadow-[0_30px_80px_-30px_color-mix(in_oklab,var(--gold-deep)_45%,transparent)] backdrop-blur sm:px-12 sm:py-14">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
            <Crown className="h-8 w-8 text-foreground" strokeWidth={2.25} />
          </div>

          <p className="mt-5 inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/40 bg-background/70 px-3.5 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-[var(--gold-deep)]">
            Step 4 of 4 · Member
          </p>

          <h1 className="mt-5 font-display text-3xl leading-tight text-foreground sm:text-[38px]">
            You're in. Welcome to Recovery Trauma™.
          </h1>
          <p className="mx-auto mt-4 max-w-lg text-[16px] leading-relaxed text-muted-foreground sm:text-[17px]">
            Your Membership is confirmed and your access is ready now.
            We've sent your confirmation to {email ? <span className="font-medium text-foreground">{email}</span> : "your inbox"}.
          </p>

          <ul className="mx-auto mt-8 flex max-w-md flex-col gap-3 text-start text-[15px] leading-relaxed text-foreground/85">
            <li className="flex items-start gap-3">
              <Bullet />{" "}
              {plan?.isFounding ? "Founding rate: " : "Your plan: "}
              <span className="font-medium text-foreground">
                {plan?.priceLabel ?? "$14.99 USD/month"}
              </span>{" "}
              · monthly subscription · cancel anytime.
            </li>
            <li className="flex items-start gap-3">
              <Bullet />{" "}
              {plan?.cancelAtPeriodEnd
                ? "Your access runs until"
                : "Renews automatically on"}{" "}
              <span className="font-medium text-foreground">
                {plan?.nextChargeLabel ?? "your next billing date"}
              </span>
              .
            </li>

            <li className="flex items-start gap-3">
              <Bullet /> Full platform access is already unlocked — nothing
              more to do.
            </li>
          </ul>

          <div className="mt-9 flex flex-col items-stretch gap-3 sm:items-center">
            <Link
              to="/my-membership"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--gradient-gold)] px-8 py-4 font-display text-[16px] font-medium text-foreground shadow-[var(--shadow-gold)] transition-all duration-300 hover:-translate-y-0.5 sm:w-auto sm:px-10 sm:text-[17px]"
            >
              <Sparkles className="h-4 w-4" /> View my membership
            </Link>
            <a
              href="mailto:support@recoverytrauma.com"
              className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              <Mail className="h-4 w-4" /> Contact support
            </a>
            <button
              type="button"
              onClick={signOut}
              className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Bullet() {
  return (
    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
      <Check className="h-3 w-3 text-foreground" strokeWidth={3} />
    </span>
  );
}
